import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLocation, useNavigate } from "react-router-dom";

/* ===== API base (as requested) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  listCategories: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/categories${qs}`);
  },
  listPrompts: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/prompts${qs}`);
  },
  trackRead: (id) => http(`/api/prompts/${id}/track-read`, { method: "POST" }),
};

/* ===== Layout Shell ===== */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
`;
const PageMain = styled.main`
  padding-top: 80px;
  flex: 1 0 auto;
  @media (max-width: 768px) {
    padding-top: 70px;
  }
  @media (max-width: 480px) {
    padding-top: 60px;
  }
`;

/* ===== Header ===== */
const HeaderSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, #3d7294 0%, #2a5a7a 100%);
  color: #fff;
  text-align: center;
  padding: clamp(2.4rem, 7vw, 3rem) 1rem;
`;
const HeaderContent = styled.div`
  position: relative;
  max-width: 980px;
  margin: 0 auto;
`;
const Title = styled.h1`
  margin: 0.75rem 0 0.6rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  line-height: 1.12;
  font-size: clamp(2rem, 5.8vw, 3rem);
  background: linear-gradient(
    90deg,
    #ffffff 0%,
    #ffe7db 35%,
    #ffb084 70%,
    #ffffff 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;
const Subtitle = styled.p`
  margin: 0.85rem auto 0;
  opacity: 0.98;
  font-size: clamp(1rem, 2.4vw, 1.15rem);
  max-width: 820px;
`;

const WarningBox = styled.div`
  margin: 1.2rem auto 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%);
  color: rgba(255,255,255,0.9);
  border: 1px solid rgba(255,255,255,0.18);
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  line-height: 1.45;
  max-width: 780px;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  backdrop-filter: blur(4px);
  text-align: left;
`;

const WarningIcon = styled.div`
  font-size: 1.25rem;
  line-height: 1;
  opacity: 0.95;
`;



/* ===== Category Tiles ===== */
const CategoriesWrap = styled.section`
  max-width: 1100px;
  margin: 0.75rem auto 0;
  padding: 0 1rem;
`;
const TilesScroller = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  scrollbar-width: thin;
  padding: 0.25rem 0;
`;
const Tile = styled.button`
  appearance: none;
  border: 1px solid
    ${({ active }) => (active ? "rgba(61,114,148,.55)" : "#e2e8f0")};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg,#3d7294 0%,#2a5a7a 100%)"
      : "#fff"};
  color: ${({ active }) => (active ? "#fff" : "#0f172a")};
  border-radius: 16px;
  padding: 0.7rem 0.9rem;
  height: 56px;
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 0.6rem;
  text-align: left;
  flex: 0 0 auto;
  cursor: pointer;
  box-shadow: ${({ active }) =>
    active
      ? "0 10px 28px rgba(61,114,148,.28)"
      : "0 3px 12px rgba(15,23,42,.06)"};
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    border-color 0.2s ease, background 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;
const TileIcon = styled.span`
  font-size: 1.25rem;
  display: grid;
  place-items: center;
`;
const TileLabel = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
`;

/* ===== List Layout ===== */
const GridSection = styled.section`
  padding: clamp(1.2rem, 4vw, 2rem) 1rem;
`;
const GridInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;
const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  background: #ffffff;
  padding: 0.85rem;
  border-radius: 12px;
  border: 1px solid rgba(2, 6, 23, 0.06);
  box-shadow: 0 6px 30px rgba(15, 23, 42, 0.04);
  transition: transform 0.14s ease;
  &:hover {
    transform: translateY(-3px);
  }
`;
const ListLeft = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 1.7rem;
  background: linear-gradient(135deg, #f5f9fb, #eef6fb);
  flex-shrink: 0;
`;
const ListContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;
const ListTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
`;
const ListTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ListDesc = styled.p`
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.35;
  max-height: 3.4rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.35rem;
`;
const Tag = styled.span`
  font-size: 0.72rem;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  color: #334155;
  background: #f8fafc;
`;

/* ===== Actions & Meta ===== */
const ListActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
`;
const SmallBtn = styled.button`
  border: 0;
  border-radius: 10px;
  padding: 0.45rem 0.7rem;
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  font-size: 0.92rem;
  box-shadow: 0 8px 20px rgba(255, 145, 77, 0.18);
  &:hover {
    transform: translateY(-1px);
  }
`;
const Meta = styled.span`
  color: #94a3b8;
  font-size: 0.82rem;
`;

/* ===== TimeSaved pill ===== */
const TimeSavedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 800;
  color: #0f172a;
  padding: 0.28rem 0.5rem;
  border-radius: 999px;
  background: radial-gradient(
      120% 120% at -10% -20%,
      #fff0 0%,
      #fff0 60%,
      #ffffff66 61%,
      #ffffffaa 68%,
      #ffffff 74%
    ),
    linear-gradient(135deg, #e6f6ff 0%, #dff3ff 35%, #fdf2e9 100%);
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(2, 6, 23, 0.06);
  white-space: nowrap;
`;

/* ===== Empty/Loading ===== */
const EmptyWrap = styled.div`
  grid-column: 1 / -1;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(2, 6, 23, 0.06);
  box-shadow: 0 6px 30px rgba(15, 23, 42, 0.06);
  padding: clamp(1rem, 4vw, 2rem);
  display: grid;
  place-items: center;
  text-align: center;
`;
const EmptyEmoji = styled.div`
  font-size: clamp(2rem, 6vw, 3rem);
  margin-bottom: 0.6rem;
`;
const EmptyTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: #0f172a;
`;
const EmptyText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
`;

/* ===== Modal ===== */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: grid;
  place-items: center;
  padding: 1rem;
  z-index: 9999;
`;
const ModalCard = styled.div`
  width: 100%;
  max-width: 760px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  background: linear-gradient(135deg, #3d7294 0%, #2a5a7a 100%);
  color: #fff;
`;
const ModalTitle = styled.h4`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
`;
const CloseBtn = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 1.25rem;
  padding: 0.25rem 0.35rem;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;
const ModalBody = styled.div`
  padding: 1rem;
  display: grid;
  gap: 0.9rem;
  max-height: 70vh;
  overflow: auto;
`;
const Section = styled.section`
  background: #0f172a08;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem;
`;
const SectionHeading = styled.h5`
  margin: 0 0 0.35rem;
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 800;
`;
const SectionText = styled.p`
  margin: 0;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;
const PromptBlock = styled.pre`
  margin: 0;
  background: #0f172a0a;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  max-height: 40vh;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.92rem;
  color: #0f172a;
`;
const ModalFooter = styled.div`
  padding: 0.75rem 1rem 1rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
const GhostSmall = styled.button`
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  padding: 0.45rem 0.6rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover {
    background: #f8fafc;
  }
`;

/* ===== Utils ===== */
const formatReads = (n) => {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (n >= 1_000)
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${n || 0}`;
};

// Simple emoji mapping by category slug
const EMOJI_BY_SLUG = {
  estimating: "📐",
  scheduling: "🗓️",
  "safety-qa": "🦺",
  safety: "🦺",
  coordination: "🤝",
  "contracts-docs": "📑",
  contracts: "📑",
};

const PromptGallery = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [modal, setModal] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load categories once
  useEffect(() => {
    (async () => {
      try {
        const res = await api.listCategories({
          page: 1,
          pageSize: 200,
          sort: "nameAsc",
        });
        const cats = (res.data || []).map((c) => ({
          _id: c._id,
          name: c.name,
          slug: c.slug,
          emoji: EMOJI_BY_SLUG[c.slug] || "✨",
        }));
        setCategories(cats);
      } catch {
        // non-blocking
      }
    })();
  }, []);

  // Handle URL ?cat
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("cat");
    const validSlugs = ["all", ...categories.map((c) => c.slug)];
    if (cat && validSlugs.includes(cat)) {
      setActive(cat);
    } else {
      const params2 = new URLSearchParams(location.search);
      params2.set("cat", "all");
      navigate({ search: params2.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, categories]);

  // Fetch prompts whenever category changes
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const params = {
          page: 1,
          pageSize: 500,
          sort: "readsDesc",
          type: "Free",
        };
        if (active !== "all") params.category = active;
        const res = await api.listPrompts(params);
        setPrompts(res.data || []);
      } catch (e) {
        setErr(e.message || "Failed to load prompts");
        setPrompts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [active]);

  const items = useMemo(() => {
    return (prompts || []).map((p) => ({
      id: p._id,
      title: p.title,
      desc: p.desc,
      detailedDesc: p.detailedDesc,
      timeSaved: p.timeSaved,
      prompt: p.prompt,
      reads: p.reads || 0,
      tags: (p.tags || []).map((t) => t.name),
      emoji: EMOJI_BY_SLUG[p?.category?.slug] || "✨",
      categorySlug: p?.category?.slug,
    }));
  }, [prompts]);

  const isEmptyCategory =
    !loading && !err && items.length === 0;

  const openPrompt = async (card) => {
    if (!card) return;
    setModal({
      id: card.id,
      title: card.title,
      detailedDesc: card.detailedDesc || "",
      timeSaved: card.timeSaved || "",
      prompt: card.prompt,
    });
    setCopied(false);
    try {
      const res = await api.trackRead(card.id);
      const newReads = res?.data?.reads ?? null;
      if (typeof newReads === "number") {
        setPrompts((prev) =>
          prev.map((x) =>
            x._id === card.id ? { ...x, reads: newReads } : x
          )
        );
      }
    } catch {
      /* non-blocking */
    }
  };

  const closeModal = () => setModal(null);

  const copyPrompt = async () => {
    if (!modal?.prompt) return;
    try {
      await navigator.clipboard.writeText(modal.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = modal.prompt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <>
      <Navbar />
      <PageShell>
        <PageMain>
          <HeaderSection>
            <HeaderContent>
              <Title>AI Prompt Gallery</Title>
              <Subtitle>
                Prompts for construction workflows
              </Subtitle>

              {/* ================= WARNING DISCLAIMER INSERTED HERE ================= */}
              <WarningBox>
                <WarningIcon>⚠️</WarningIcon>
                <div>
                  <strong>Disclaimer:</strong> These AI prompts are for
                  training and educational purposes only. <br />
                  Always validate and review AI-generated content before
                  using it in real construction projects or decision-making.
                </div>
              </WarningBox>
              {/* =================================================================== */}

            </HeaderContent>
          </HeaderSection>

          <CategoriesWrap>
            <TilesScroller aria-label="Prompt categories">
              {categories.map((cat) => (
                <Tile
                  key={cat.slug}
                  active={active === cat.slug}
                  onClick={() => {
                    const params = new URLSearchParams(location.search);
                    params.set("cat", cat.slug);
                    navigate({ search: params.toString() }, { replace: true });
                  }}
                  aria-pressed={active === cat.slug}
                >
                  <TileIcon>{cat.emoji}</TileIcon>
                  <TileLabel>{cat.name}</TileLabel>
                </Tile>
              ))}
            </TilesScroller>
          </CategoriesWrap>

          <GridSection>
            <GridInner>
              {loading ? (
                <EmptyWrap>
                  <EmptyEmoji>⏳</EmptyEmoji>
                  <EmptyTitle>Loading prompts…</EmptyTitle>
                  <EmptyText>Please wait a moment.</EmptyText>
                </EmptyWrap>
              ) : err ? (
                <EmptyWrap>
                  <EmptyEmoji>⚠️</EmptyEmoji>
                  <EmptyTitle>Couldn’t load prompts</EmptyTitle>
                  <EmptyText>{err}</EmptyText>
                </EmptyWrap>
              ) : isEmptyCategory ? (
                <EmptyWrap>
                  <EmptyEmoji>🔎</EmptyEmoji>
                  <EmptyTitle>No prompts found</EmptyTitle>
                  <EmptyText>
                    Try switching categories — or select{" "}
                    <strong>All</strong> to see every available prompt.
                  </EmptyText>
                </EmptyWrap>
              ) : (
                <ListWrap>
                  {items.map((c) => (
                    <ListItem key={c.id}>
                      <ListLeft>{c.emoji}</ListLeft>
                      <ListContent>
                        <ListTitleRow>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              minWidth: 0,
                            }}
                          >
                            <ListTitle title={c.title}>{c.title}</ListTitle>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            {c.timeSaved?.trim() ? (
                              <TimeSavedPill title="Estimated time saved">
                                <span role="img" aria-label="stopwatch">
                                  ⏱
                                </span>
                                <span>
                                  Estimated Time Saved - {c.timeSaved}
                                </span>
                              </TimeSavedPill>
                            ) : null}
                            <Meta>
                              👁 {formatReads(c.reads || 0)}
                            </Meta>
                          </div>
                        </ListTitleRow>
                        <ListDesc>{c.desc}</ListDesc>
                        <Tags>
                          {(c.tags || [])
                            .slice(0, 4)
                            .map((t, idx) => (
                              <Tag key={idx}>{t}</Tag>
                            ))}
                        </Tags>
                      </ListContent>
                      <ListActions>
                        <SmallBtn onClick={() => openPrompt(c)}>
                          Use Prompt
                        </SmallBtn>
                      </ListActions>
                    </ListItem>
                  ))}
                </ListWrap>
              )}
            </GridInner>
          </GridSection>
        </PageMain>

        <Footer />
      </PageShell>

      {modal && (
        <ModalOverlay
          role="dialog"
          aria-modal="true"
          aria-labelledby="promptTitle"
          onClick={closeModal}
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="promptTitle">{modal.title}</ModalTitle>
              <CloseBtn aria-label="Close" onClick={closeModal}>
                ✕
              </CloseBtn>
            </ModalHeader>
            <ModalBody>
              {modal.timeSaved?.trim() ? (
                <Section>
                  <SectionHeading>Estimated Time Saved</SectionHeading>
                  <SectionText>⏱ {modal.timeSaved}</SectionText>
                </Section>
              ) : null}
              <Section>
                <SectionHeading>What this prompt does?</SectionHeading>
                <SectionText>
                  {modal.detailedDesc?.trim()
                    ? modal.detailedDesc
                    : "No detailed description yet."}
                </SectionText>
              </Section>
              <Section>
                <SectionHeading>Prompt</SectionHeading>
                <PromptBlock>{modal.prompt}</PromptBlock>
              </Section>
            </ModalBody>
            <ModalFooter>
              <GhostSmall onClick={copyPrompt}>
                {copied ? "Copied ✓" : "Copy Prompt"}
              </GhostSmall>
              <GhostSmall onClick={closeModal}>Close</GhostSmall>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default PromptGallery;
